import type { ConceptualLastEdit } from '../types/conceptualProblems';

/**
 * How an editor's name is written on the conceptual-problems page (feature 029, FR-047).
 *
 * Two lengths, because the page has two very different amounts of room: the desktop status line
 * and the ⓘ popup have space for the whole name, and the phone masthead row has about seven
 * characters' worth beside its icons. The short form is the first name and the last initial —
 * "Jane D." — which is what an editing team that knows each other actually calls one another, and
 * stays readable where initials alone ("JD") collide.
 */

/** "Jane Doe". Either half may be blank on an old account; the other half is then used alone. */
export function editorFullName(edit: Pick<ConceptualLastEdit, 'firstName' | 'lastName'>): string {
  return [edit.firstName.trim(), edit.lastName.trim()].filter(Boolean).join(' ');
}

/** "Jane D.", for the phone masthead. A missing last name gives just "Jane". */
export function editorShortName(edit: Pick<ConceptualLastEdit, 'firstName' | 'lastName'>): string {
  const first = edit.firstName.trim();
  const last = edit.lastName.trim();

  if (!first) return last;
  if (!last) return first;

  // `Array.from` rather than `last[0]`, so a name starting with a character outside the Basic
  // Multilingual Plane keeps its whole first character instead of half a surrogate pair.
  return `${first} ${Array.from(last)[0]!.toUpperCase()}.`;
}

/** "16 Sep 2026" — the day of the save, in the site's British date order. */
export function editDate(at: string): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * "Jane Doe, 16 Sep 2026" — the name with the day of the save. For the ⓘ popup and the pills'
 * tooltips only: the date is kept off the main screen, where the status line and the phone masthead
 * have no room for it (client, 2026-09-17).
 */
export function editorNameWithDate(edit: ConceptualLastEdit): string {
  const date = editDate(edit.at);
  return date ? `${editorFullName(edit)}, ${date}` : editorFullName(edit);
}
